
import React from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { DIFFICULTY_CONFIGS, DifficultyRank, getDifficultyConfig } from "@/utils/difficultyRanks";
import { Trophy, Target, Mountain, Flame, Crown } from "lucide-react";
import HelpTooltip from "@/components/help/HelpTooltip";

interface DifficultySelectorProps {
  value?: DifficultyRank | null;
  onChange: (rank: DifficultyRank) => void;
  disabled?: boolean;
}

const DifficultySelector: React.FC<DifficultySelectorProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const { enableRpgMode, darkMode } = useSettings();

  const getDifficultyIcon = (rank: DifficultyRank) => {
    const iconProps = { className: "h-4 w-4" };
    switch (rank) {
      case 'quick_win': return <Trophy {...iconProps} />;
      case 'simple': return <Target {...iconProps} />;
      case 'moderate': return <Mountain {...iconProps} />;
      case 'challenging': return <Flame {...iconProps} />;
      case 'major': return <Crown {...iconProps} />;
      default: return <Mountain {...iconProps} />;
    }
  };

  const tooltipContent = (
    <div className="space-y-2 max-w-sm">
      <p className="font-medium text-sm mb-2">Challenge Level helps you set expectations:</p>
      <div className="space-y-1 text-xs">
        <div><strong>Quick Win:</strong> Small daily decisions (5-15 min)</div>
        <div><strong>Simple:</strong> Minor workplace issues (30 min - 1 hour)</div>
        <div><strong>Moderate:</strong> Important life choices (1-3 hours)</div>
        <div><strong>Challenging:</strong> Complex relationship or career decisions (several hours to days)</div>
        <div><strong>Major:</strong> Life-changing situations you've been stuck on for weeks/months</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-2" data-tutorial="difficulty-selector">
      <Label className={cn(
        "flex items-center gap-2",
        enableRpgMode && darkMode ? "text-amber-200" : enableRpgMode ? "text-amber-800" : ""
      )}>
        {enableRpgMode ? "Quest Difficulty" : "Challenge Level"}
        <HelpTooltip 
          content={tooltipContent}
          side="right"
        />
      </Label>
      <Select
        value={value || 'moderate'}
        onValueChange={(newValue) => onChange(newValue as DifficultyRank)}
        disabled={disabled}
      >
        <SelectTrigger className={cn(
          enableRpgMode && [
            darkMode ? "bg-amber-900/20 border-amber-600/50" : "bg-amber-50 border-amber-300",
            "focus:border-amber-500"
          ]
        )}>
          <SelectValue placeholder="Select difficulty..." />
        </SelectTrigger>
        <SelectContent>
          {Object.values(DIFFICULTY_CONFIGS).map((config) => (
            <SelectItem key={config.rank} value={config.rank}>
              <div className="flex items-center gap-2">
                <span className={cn(
                  enableRpgMode ? config.rpgColor : config.color
                )}>
                  {getDifficultyIcon(config.rank)}
                </span>
                <div className="flex flex-col">
                  <span className="font-medium">{config.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {config.description} • {config.pips} {enableRpgMode ? "milestones" : "steps"}
                  </span>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default DifficultySelector;
